import React from 'react';
import { toast as sonnerToast } from 'sonner';
import { useToastTimer } from './useToastTimer';

/**
 * Toast Kadélio, direction A « Carte claire » (maquettes To-2, To-5, décision du 16/10) :
 * carte blanche, la couleur ne vit que dans une pastille de 32 px avec une forme par
 * sens (coche, « ! », « i », triangle), action en lien indigo, croix « Fermer » de 44 px,
 * barre de temps de 3 px sous « Annuler ». Habillage : src/styles/toasts.css.
 */
export type ToastKind = 'success' | 'error' | 'info' | 'warning' | 'loading';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

const PATHS: Record<ToastKind, React.ReactNode> = {
  success: <path d="M20 6 9 17l-5-5" />,
  error: <path d="M12 7v6.5M12 17h.01" />,
  info: <path d="M12 11v6M12 7h.01" />,
  warning: (
    <>
      <path d="M12 3.5 2.5 20h19z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  loading: <path d="M21 12a9 9 0 1 1-6.2-8.6" />,
};

/** Pastille ronde du sens, partagée par les toasts et les bandeaux (36 px). */
export function ToastPastille({ kind, size = 32, className }: { kind: ToastKind; size?: 32 | 36; className?: string }) {
  const icon = size === 36 ? 20 : 18;
  return (
    <span aria-hidden className={['kd-pastille', className].filter(Boolean).join(' ')} data-kind={kind} data-size={size}>
      {kind === 'loading' && <span className="kd-pastille-dots"><i /><i /><i /></span>}
      <svg
        width={icon}
        height={icon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={size === 36 ? 2.4 : 2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={kind === 'loading' ? 'kd-pastille-spin' : undefined}
      >
        {PATHS[kind]}
      </svg>
    </span>
  );
}

export function CloseIcon() {
  return (
    <svg aria-hidden width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export interface ToastCardProps {
  kind: ToastKind;
  message: React.ReactNode;
  action?: ToastAction;
  onClose?: () => void;
  /** Durée de la barre de temps (ms). Absente : pas de barre. */
  timerMs?: number;
  paused?: boolean;
  /** Contenu en plus sous le message (proposition de cagnotte du catalogue). */
  extra?: React.ReactNode;
  className?: string;
  testId?: string;
  handlers?: React.HTMLAttributes<HTMLDivElement>;
}

/** Carte seule (sans position) : utilisée par la région Notifications et par la feuille d'ajout. */
export function ToastCard({ kind, message, action, onClose, timerMs, paused, extra, className, testId, handlers }: ToastCardProps) {
  const seconds = timerMs ? Math.round(timerMs / 1000) : 0;
  return (
    <div className={['kd-toast-wrap', className].filter(Boolean).join(' ')} data-testid={testId}>
      <div
        role={kind === 'error' ? 'alert' : 'status'}
        className="kd-toast"
        data-kind={kind}
        data-timer={timerMs ? 'true' : undefined}
        data-paused={paused ? 'true' : undefined}
        {...handlers}
      >
        <ToastPastille kind={kind} className="kd-toast-pastille" />
        <p className="kd-toast-text">{message}</p>
        {action && (
          <div className="kd-toast-act">
            {timerMs ? <span aria-hidden className="kd-toast-secs">{`${seconds}\u00A0s`}</span> : null}
            <button type="button" className="kd-toast-action" onClick={action.onClick}>
              {action.label}
            </button>
          </div>
        )}
        {onClose && kind !== 'loading' ? (
          <button type="button" aria-label="Fermer" className="kd-toast-close" onClick={onClose}>
            <CloseIcon />
          </button>
        ) : null}
        {extra ? <div className="kd-toast-extra">{extra}</div> : null}
        {timerMs ? (
          <span aria-hidden className="kd-toast-track">
            <span
              className="kd-toast-bar"
              data-testid="toast-timer-bar"
              style={{ animationDuration: `${timerMs}ms`, animationPlayState: paused ? 'paused' : 'running' }}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
}

export interface RegionToastSpec {
  kind: ToastKind;
  message: React.ReactNode;
  duration: number;
  action?: ToastAction;
  timer?: boolean;
}

/** Toast de la région « Notifications » (sonner) : carte + minuterie + fermeture. */
export function RegionToast({ id, spec }: { id: string | number; spec: RegionToastSpec }) {
  const { paused, handlers } = useToastTimer(spec.duration, () => sonnerToast.dismiss(id));
  const action = spec.action
    ? { label: spec.action.label, onClick: () => { sonnerToast.dismiss(id); spec.action!.onClick(); } }
    : undefined;
  return (
    <ToastCard
      kind={spec.kind}
      message={spec.message}
      action={action}
      onClose={() => sonnerToast.dismiss(id)}
      timerMs={spec.timer ? spec.duration : undefined}
      paused={paused}
      handlers={handlers}
    />
  );
}
